<script setup>
import { defineProps, defineEmits, computed } from 'vue';
import { getCurrentInstance } from 'vue';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Checkbox from 'primevue/checkbox';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';

const { proxy } = getCurrentInstance();
const { formatWithSymbol, roundWithCents } = useCurrencyFormatter();
const $t = proxy.$t;

// Helper function to convert currency using exchange rates
const convertCurrency = (amount, fromCurrency, toCurrency) => {
    if (!props.exchangeData || fromCurrency === toCurrency) {
        return amount;
    }

    const fromCurrencyUpper = fromCurrency?.toUpperCase();
    const toCurrencyUpper = toCurrency?.toUpperCase();
    
    const fromExchange = props.exchangeData[fromCurrencyUpper];
    const toExchange = props.exchangeData[toCurrencyUpper];
    
    if (!fromExchange || !toExchange) {
        return amount; // Return original amount if exchange data not found
    }

    const fromRate = (fromExchange.value || 100) / 100;
    const toRate = (toExchange.value || 100) / 100;
    
    // Convert amount to USD, then to target currency
    const usdAmount = amount / fromRate;
    const convertedAmount = usdAmount * toRate;
    
    return convertedAmount;
};

// Calculate suggested price based on cost, sale currency and exchange data
const getSuggestedPrice = (typeData) => {
    if (!props.exchangeData || !typeData || !typeData.cost || !typeData.sale_coin) {
        return '-';
    }

    const costString = typeData.cost?.toString() || '';
    if (!costString.trim()) return '-';
    
    const cleanCost = costString.replace(',', '.');
    const costValue = parseFloat(cleanCost);
    
    if (isNaN(costValue) || costValue <= 0) return '-';

    // Get cost currency (default to BRL if not specified)
    const costCurrency = typeData.cost_coin || 'BRL';
    const saleCurrency = typeData.sale_coin?.toUpperCase();
    
    // Convert cost from cost_coin to sale_coin
    const convertedCost = convertCurrency(costValue, costCurrency, saleCurrency);
    
    // Get exchange data for the sale currency
    const exchangeInfo = props.exchangeData[saleCurrency];
    
    if (!exchangeInfo) return '-';

    const fixedAddition = (exchangeInfo.fixed_addition || 0) / 100;
    const percentageAddition = (exchangeInfo.percentage_addition || 0) / 10000;
    const applyPercentageToTotal = exchangeInfo.apply_percentage_to_total || false;

    let suggestedPrice = 0;

    if (applyPercentageToTotal) {
        // Formula Type C: (COST + FIXED) × (1 + %)
        suggestedPrice = (convertedCost + fixedAddition) * (1 + percentageAddition);
    } else {
        // Formula Type B: (COST × (1 + %)) + FIXED (or Type A if no percentage)
        suggestedPrice = (convertedCost * (1 + percentageAddition)) + fixedAddition;
    }

    return roundWithCents(suggestedPrice);
};

// Format suggested price with currency symbol
const formatSuggestedPrice = (typeData) => {
    const price = getSuggestedPrice(typeData);
    if (price === '-') return '-';

    const currency = typeData.sale_coin || typeData.cost_coin || 'USD';
    const numPrice = parseFloat(price);

    return formatWithSymbol(numPrice, currency);
};

const props = defineProps({
    types: {
        type: Array,
        required: true
    },
    currencyOptions: {
        type: Array,
        required: true
    },
    // For Default type in Create mode
    defaultCost: {
        type: String,
        default: ''
    },
    defaultCostCoin: {
        type: String,
        default: 'BRL'
    },
    defaultSale: {
        type: String,
        default: ''
    },
    defaultSaleCoin: {
        type: String,
        default: 'BRL'
    },
    formatCurrency: {
        type: Function,
        required: true
    },
    // Flag to determine if we're in edit mode (different structure)
    isEditMode: {
        type: Boolean,
        default: false
    },
    // For Edit mode - seller info and permissions
    canEdit: {
        type: Boolean,
        default: true
    },
    showAvailableColumn: {
        type: Boolean,
        default: true
    },
    
    showRequiredMessage: {
        type: Boolean,
        default: true
    },
    exchangeData: {
        type: Object,
        default: () => ({})
    },
    site: {
        type: Object,
        default: () => ({})
    },
    // Sellers list for Create mode
    sellers: {
        type: Array,
        default: () => []
    },
    // Selected seller ID
    selectedSeller: {
        type: [Number, String, null],
        default: null
    },
    isExternalSellerEditMode: {
        type: Boolean,
        default: false
    },
    isLinkarme: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['update:defaultCost', 'update:defaultCostCoin', 'update:defaultSale', 'update:defaultSaleCoin', 'update:selectedSeller', 'toggleExternalEdit']);

const isHighestPriceApplied = (type) => {
    const highest = highestPrice.value.match(/[\d,]+\.?\d*/)?.[0]?.replace(/,/g, '');
    const current = type.cost?.toString().replace(',', '.');
    return highest && current && Math.abs(parseFloat(current) - parseFloat(highest)) < 0.01;
};

const isSuggestedPriceApplied = (type) => {
    const suggested = getSuggestedPrice(type);
    const current = type.sale?.toString().replace(',', '.');
    return suggested !== '-' && current && Math.abs(parseFloat(current) - parseFloat(suggested)) < 0.01;
};

const dynamicCurrencyOptions = computed(() => {
    if (!props.exchangeData) return [];
    
    const currencyList = [];
    for (const code in props.exchangeData) {
        const currency = code.toUpperCase();
        currencyList.push({
            label: currency,
            value: currency
        });
    }
    return currencyList;
});

// Handle updates for default type values
const updateDefaultCost = (value) => {
    emit('update:defaultCost', value);
};

const updateDefaultCostCoin = (value) => {
    emit('update:defaultCostCoin', value);
};

const updateDefaultSale = (value) => {
    emit('update:defaultSale', value);
};

const updateDefaultSaleCoin = (value) => {
    emit('update:defaultSaleCoin', value);
};

const updateSelectedSeller = (value) => {
    emit('update:selectedSeller', value);
};

const getCurrentAnchorValue = () => {
    // Get the accepted_anchors value from the first available type
    const availableType = props.types.find(type => type.available);
    return availableType ? Math.max(availableType.accepted_anchors || 1, 1) : 1;
};

const updateAcceptedAnchors = (value) => {
    const anchorValue = Math.max(value, 1);
    
    // Apply the same accepted_anchors value to all available types
    props.types.forEach(type => {
        type.accepted_anchors = anchorValue;
    });
};

const applySuggestedPrice = (itemData) => {
    const suggested = getSuggestedPrice(itemData);
    itemData.sale = suggested;
};

const applyDefaultSuggestedPrice = () => {
    const defaultTypeData = {
        cost: props.defaultCost,
        cost_coin: props.defaultCostCoin,
        sale_coin: props.defaultSaleCoin
    };

    if (!props.exchangeData || !defaultTypeData || !defaultTypeData.cost || !defaultTypeData.sale_coin) {
        return '-';
    }

    const suggested = getSuggestedPrice(defaultTypeData);
    if (!suggested) return '-';

    updateDefaultSale(suggested);
};

const highestPrice = computed(() => {
    if (!props.types?.length || !props.exchangeData) return '-';

    let highest = null;
    let highestInUSD = 0;

    for (const t of props.types) {
        const rawValue = parseFloat(t.cost?.toString().replace(',', '.') || 0);
        if (isNaN(rawValue) || rawValue <= 0) continue;

        const currency = t.cost_coin?.toUpperCase() || 'USD';
        const convertedToUSD = convertCurrency(rawValue, currency, 'USD');

        if (convertedToUSD > highestInUSD) {
            highestInUSD = convertedToUSD;
            highest = { value: rawValue, currency };
        }
    }

    if (!highest) return '-';

    return formatWithSymbol(highest.value, highest.currency);
});

const syncCurrencies = (itemData, newCurrency, isFromCost = true) => {
    if (isFromCost) {
        itemData.sale_coin = newCurrency;
    } else {
        itemData.cost_coin = newCurrency;
    }
};

const syncDefaultCurrencies = (newCurrency, isFromCost = true) => {
    if (isFromCost) {
        updateDefaultSaleCoin(newCurrency);
    } else {
        updateDefaultCostCoin(newCurrency);
    }
};

const syncAnchorsForNewType = (typeData) => {
    if (typeData.available) {
        const currentAnchorValue = getCurrentAnchorValue();
        
        typeData.accepted_anchors = Math.max(currentAnchorValue, 1);
        
        updateAcceptedAnchors(currentAnchorValue);
    }
};

// Check if selected seller is Linkarme
const isLinkarmeSelected = computed(() => {
    if (!props.selectedSeller || !props.sellers) return false;
    
    const selectedSellerObj = props.sellers.find(seller => seller.id === props.selectedSeller);
    return selectedSellerObj?.linkarme_seller === true || selectedSellerObj?.linkarme_seller === 1;
});

const canEditCost = computed(() => {
    if (!props.isEditMode) {
        return props.canEdit;
    }

    if (props.isLinkarme) {
        return props.canEdit;
    }
    return props.isExternalSellerEditMode;
});

const showSaleColumn = computed(() => {
    return props.isLinkarme && props.canEdit;
});

const showExternalEditButton = computed(() => {
    return props.isEditMode && !props.isLinkarme;
});

const toggleExternalEdit = () => {
    emit('toggleExternalEdit');
};

const getSaleInputClass = (typeData) => {
    if (!typeData.available) return '';
    
    const cleanValue = typeData.sale?.toString().replace(',', '.');
    const numValue = parseFloat(cleanValue);
    
    if (typeData.sale && (isNaN(numValue) || numValue <= 0)) {
        return 'border-red-500';
    }
    
    return '';
};

const getFormulaDescription = (typeData) => {
    if (!props.exchangeData || !typeData) return '-';
    
    const currency = typeData.cost_coin?.toUpperCase() || 'BRL';
    const exchangeInfo = props.exchangeData[currency];
    
    if (!exchangeInfo) return '-';
    
    const fixedAddition = (exchangeInfo.fixed_addition || 0) / 100;
    const percentageAddition = (exchangeInfo.percentage_addition || 0) / 100;
    const applyPercentageToTotal = exchangeInfo.apply_percentage_to_total || false;
    
    if (applyPercentageToTotal) {
        return `(COST + ${fixedAddition.toFixed(2)}) × ${(1 + percentageAddition / 100).toFixed(2)}`;
    } else {
        return `(COST × ${(1 + percentageAddition / 100).toFixed(2)}) + ${fixedAddition.toFixed(2)}`;
    }
};

const getCalculatedPrice = (typeData) => {
    if (!props.exchangeData || !typeData || !typeData.cost) return '-';
    
    const costString = typeData.cost?.toString() || '';
    if (!costString.trim()) return '-';
    
    const cleanCost = costString.replace(',', '.');
    const costValue = parseFloat(cleanCost);
    
    if (isNaN(costValue) || costValue <= 0) return '-';
    
    const currency = typeData.cost_coin?.toUpperCase() || 'BRL';
    const exchangeInfo = props.exchangeData[currency];
    
    if (!exchangeInfo) return '-';
    
    const fixedAddition = (exchangeInfo.fixed_addition || 0) / 100;
    const percentageAddition = (exchangeInfo.percentage_addition || 0) / 10000;
    const applyPercentageToTotal = exchangeInfo.apply_percentage_to_total || false;
    
    let calculatedPrice = 0;
    
    if (applyPercentageToTotal) {
        calculatedPrice = (costValue + fixedAddition) * (1 + percentageAddition);
    } else {
        calculatedPrice = (costValue * (1 + percentageAddition)) + fixedAddition;
    }
    
    calculatedPrice = roundWithCents(calculatedPrice);

    return formatWithSymbol(calculatedPrice, currency);
};

const showFormulaColumns = computed(() => {
    return props.isEditMode && !props.isLinkarme;
});

</script>

<template>
    <div class="pricing-table-component">
        <div class="top-section flex justify-between items-start mb-3">
            <div class="left-side">
                <div class="title mb-3">
                    <h3 class="text-base font-semibold mb-1.5">{{ $t('Pricing table') }}</h3>
                    <p v-if="showRequiredMessage && (!isEditMode && selectedSeller)" class="text-sm text-surface-600">
                        <span v-if="isLinkarmeSelected">
                            {{ $t('At least one is required') }}
                            <span class="text-sm text-red-600">*</span>
                        </span>
                        <span v-if="!isLinkarmeSelected" class="text-sm text-orange-600">
                            {{ $t('Sale prices are automatically calculated for non-Linkarme sellers') }}
                            <span class="text-sm text-red-600">*</span>
                        </span>
                    </p>
                </div>
                <div v-if="!isEditMode" class="sellers mb-3">
                    <label class="block text-sm font-medium text-surface-700 mb-2">
                        {{ $t('Seller') }}
                    </label>
                    <Select
                        :model-value="selectedSeller"
                        @update:model-value="updateSelectedSeller"
                        :options="sellers"
                        optionLabel="name"
                        optionValue="id"
                        :placeholder="$t('Select seller')"
                        class="w-48"
                        :disabled="!canEdit"
                    />
                </div>
            </div>

            <div class="flex items-center gap-4">
                <!-- Accepted Anchors Section -->
                <div v-if="isEditMode || isLinkarme || isLinkarmeSelected || isExternalSellerEditMode || (!isEditMode && selectedSeller)" class="flex items-center p-3 border border-surface-300 rounded-lg bg-surface-50">
                    <h4 class="mr-3 text-sm font-semibold">{{ $t('Accepted Anchors') + ': ' }}</h4>
                    <div class="flex gap-4">
                        <label class="flex items-center gap-2 cursor-pointer">
                            <Checkbox 
                                :model-value="getCurrentAnchorValue() === 1"
                                @update:model-value="(checked) => updateAcceptedAnchors(checked ? 1 : 1)"
                                :binary="true"
                                :disabled="!canEditCost"
                            />
                            <span class="text-sm">1</span>
                        </label>
                        <label class="flex items-center gap-2 cursor-pointer">
                            <Checkbox 
                                :model-value="getCurrentAnchorValue() === 2"
                                @update:model-value="(checked) => updateAcceptedAnchors(checked ? 2 : 1)"
                                :binary="true"
                                :disabled="!canEditCost"
                            />
                            <span class="text-sm">2</span>
                        </label>
                        <label class="flex items-center gap-2 cursor-pointer">
                            <Checkbox 
                                :model-value="getCurrentAnchorValue() === 3"
                                @update:model-value="(checked) => updateAcceptedAnchors(checked ? 3 : 1)"
                                :binary="true"
                                :disabled="!canEditCost"
                            />
                            <span class="text-sm">3</span>
                        </label>
                    </div>
                </div>
            </div>
        </div>

        <DataTable :value="[...types]" class="mb-3">
            <Column field="name" class="font-medium">
                <template #header>
                    <span class="font-bold">{{ $t('Type') }}</span>
                </template>
                <template #body="slotProps">
                    <span>{{ slotProps.data.name }}</span>
                </template>
            </Column>

            <Column v-if="showAvailableColumn && (isLinkarme || isLinkarmeSelected || isExternalSellerEditMode || (!isEditMode && selectedSeller))" :header="$t('Available')" headerClass="text-center font-bold" bodyClass="text-center">
                <template #body="slotProps">
                    <Checkbox
                        v-model="slotProps.data.available"
                        :binary="true"
                        :disabled="!canEditCost"
                        @update:model-value="() => syncAnchorsForNewType(slotProps.data)"
                    />
                </template>
            </Column>

            <Column>
                <template #header>
                    <span class="font-bold">{{ $t('Cost') }}</span>
                </template>
                <template #body="slotProps">
                    <div v-if="slotProps.data.isDefault && !isEditMode" class="flex gap-2" :class="{ 'opacity-50': !slotProps.data.available && canEditCost }">
                        <InputText
                            :model-value="defaultCost"
                            @update:model-value="updateDefaultCost"
                            class="flex-1"
                            placeholder="0,00"
                            :disabled="!slotProps.data.available || !canEditCost"
                            @blur="updateDefaultCost(formatCurrency(defaultCost))"
                        />
                        <Select
                            :model-value="defaultCostCoin"
                            @update:model-value="(value) => {
                                updateDefaultCostCoin(value);
                                if (isLinkarmeSelected) syncDefaultCurrencies(value, true);
                            }"
                            :options="dynamicCurrencyOptions"
                            optionLabel="label"
                            optionValue="value"
                            :disabled="!slotProps.data.available || !canEditCost"
                            class="w-28 items-center"
                        />
                    </div>
                    <!-- Other types or Edit mode -->
                    <div v-else class="flex gap-2" :class="{ 'opacity-50': !slotProps.data.available && canEditCost }">
                        <InputText
                            v-model="slotProps.data.cost"
                            :disabled="!slotProps.data.available || !canEditCost"
                            :class="'flex-1'"
                            placeholder="0,00"
                            @blur="slotProps.data.cost = formatCurrency(slotProps.data.cost)"
                        />
                        <Select
                            v-model="slotProps.data.cost_coin"
                            @update:model-value="(value) => {
                                slotProps.data.cost_coin = value;
                                if (isLinkarme || isLinkarmeSelected) syncCurrencies(slotProps.data, value, true);
                            }"
                            :options="dynamicCurrencyOptions"
                            optionLabel="label"
                            optionValue="value"
                            :disabled="!slotProps.data.available || !canEditCost"
                            class="w-28 items-center"
                        />
                    </div>
                    <div v-if="slotProps.data.available && canEditCost && (isLinkarme || isLinkarmeSelected)" class="text-sm text-blue-500 ml-1 justify-between flex mt-1 items-center">
                            <span class="invisible">{{ $t('Highest Price') }}</span>
                    </div>
                </template>
            </Column>

            <Column v-if="showFormulaColumns">
                <template #header>
                    <span class="font-bold">{{ $t('Formula') }}</span>
                </template>
                <template #body="slotProps">
                    <div :class="{ 'opacity-50': !slotProps.data.available }">
                        <InputText
                            :value="getFormulaDescription(slotProps.data)"
                            readonly
                            class="w-full font-mono text-xs bg-surface-50 cursor-default h-10"
                            disabled
                        />
                    </div>
                </template>
            </Column>

            <Column v-if="showFormulaColumns" headerClass="text-center" bodyClass="text-center" style="width: 100px;">
                <template #header>
                    <span class="font-bold">{{ $t('Currency') }}</span>
                </template>
                <template #body="slotProps">
                    <div :class="{ 'opacity-50': !slotProps.data.available }">
                        <InputText
                            :value="slotProps.data.cost_coin || 'BRL'"
                            readonly
                            class="w-full text-center bg-surface-50 cursor-default"
                            disabled
                        />
                    </div>
                </template>
            </Column>

            <Column v-if="showFormulaColumns">
                <template #header>
                    <span class="font-bold">{{ $t('Calculated Price') }}</span>
                </template>
                <template #body="slotProps">
                    <div :class="{ 'opacity-50': !slotProps.data.available }">
                        <InputText
                            :value="slotProps.data.available && slotProps.data.cost ? getCalculatedPrice(slotProps.data) : '-'"
                            readonly
                            class="w-full font-semibold text-primary-600 bg-surface-50 cursor-default"
                            disabled
                        />
                    </div>
                </template>
            </Column>

            <Column v-if="showSaleColumn">
                <template #header>
                    <span class="font-bold">{{ $t('Sale') }}</span>
                </template>
                <template #body="slotProps">
                    <div v-if="slotProps.data.isDefault && !isEditMode" class="flex flex-col gap-1" :class="{ 'opacity-50': !slotProps.data.available && canEdit }">
                        <div class="flex gap-2">
                            <InputText
                                :model-value="defaultSale"
                                @update:model-value="updateDefaultSale"
                                class="flex-1"
                                placeholder="0,00"
                                :disabled="!slotProps.data.available || !canEdit"
                                @blur="updateDefaultSale(formatCurrency(defaultSale))"
                            />
                            <Select
                                :model-value="defaultSaleCoin"
                                @update:model-value="(value) => {
                                    updateDefaultSaleCoin(value);
                                    syncDefaultCurrencies(value, false);
                                }"
                                :options="dynamicCurrencyOptions"
                                optionLabel="label"
                                optionValue="value"
                                :disabled="!slotProps.data.available || !canEdit"
                                class="w-28 items-center"
                            />
                        </div>
                        
                        <div v-if="defaultCost && defaultSaleCoin && slotProps.data.available" class="flex justify-between text-sm text-semibold text-blue-500 ml-1">
                            <span>
                                {{ $t('Suggested') + ': ' + formatSuggestedPrice({ cost: defaultCost, cost_coin: defaultCostCoin, sale_coin: defaultSaleCoin }) }}
                            </span>

                            <span
                                :class="isSuggestedPriceApplied({ cost: defaultCost, cost_coin: defaultCostCoin, sale: defaultSale, sale_coin: defaultSaleCoin }) 
                                    ? 'text-gray-400 cursor-default' 
                                    : 'cursor-pointer text-primary-600 underline hover:underline'"
                                @click="isSuggestedPriceApplied({ cost: defaultCost, cost_coin: defaultCostCoin, sale: defaultSale, sale_coin: defaultSaleCoin }) ? null : applyDefaultSuggestedPrice()"
                            >
                                {{ isSuggestedPriceApplied({ cost: defaultCost, cost_coin: defaultCostCoin, sale: defaultSale, sale_coin: defaultSaleCoin }) ? $t('Applied') : $t('Apply') }}
                            </span>
                        </div>
                    </div>

                    <!-- Other types or Edit mode -->
                    <div
                        v-else
                        class="flex flex-col gap-1"
                        :class="{ 'opacity-50': !slotProps.data.available && canEdit }"
                    >
                        <div class="flex gap-2">
                            <InputText
                                v-model="slotProps.data.sale"
                                :disabled="!slotProps.data.available || !canEdit"
                                :class="['flex-1', getSaleInputClass(slotProps.data)]"
                                placeholder="0,00"
                                @blur="slotProps.data.sale = formatCurrency(slotProps.data.sale)"
                            />
                            <Select
                                v-model="slotProps.data.sale_coin"
                                @update:model-value="(value) => {
                                    slotProps.data.sale_coin = value;
                                    syncCurrencies(slotProps.data, value, false);
                                }"
                                :options="dynamicCurrencyOptions"
                                optionLabel="label"
                                optionValue="value"
                                :disabled="!slotProps.data.available || !canEdit"
                                class="w-28 items-center"
                            />
                        </div>

                        <div v-if="slotProps.data.available && canEdit" class="flex justify-between text-sm text-semibold text-blue-500 ml-1">
                            <span>
                                {{ $t('Suggested') + ': ' + formatSuggestedPrice(slotProps.data) }}
                            </span>

                            <span
                                :class="isSuggestedPriceApplied(slotProps.data) 
                                    ? 'text-gray-400 cursor-default' 
                                    : 'cursor-pointer text-primary-600 underline hover:underline'"
                                @click="isSuggestedPriceApplied(slotProps.data) ? null : applySuggestedPrice(slotProps.data)"
                            >
                                {{ isSuggestedPriceApplied(slotProps.data) ? $t('Applied') : $t('Apply') }}
                            </span>
                        </div>
                    </div>
                </template>
            </Column>
        </DataTable>
    </div>
</template>

<style scoped>
.required-field {
    color: red;
}
</style>
